import { Drawer, IconButton, styled } from '@mui/material';
import { type ReactNode, forwardRef } from 'react';
import { VyButton } from '../../buttons/VyButton';
import { VyTypography } from '../../data-display/VyTypography';
import { XClose } from '../../../foundations/icons';
import { semantic } from '../../../theme/tokens/semantic';

/**
 * @tier organism
 *
 * Side-panel drawer with built-in chrome — header (title + close X +
 * optional subtitle), scrollable body, and optional footer (Cancel /
 * Save). Footer renders only when both `onCancel` and `onSave` are
 * provided.
 *
 * Reusable for any "edit this thing in a slide-out" workflow. The
 * Fixed-fees modify panel is the first consumer.
 */
export type VyDrawerProps = {
  /** Whether the drawer is open. */
  open: boolean;
  /** Called when the close X is clicked, Escape is pressed, or backdrop is clicked. */
  onClose: () => void;
  /** Edge of the screen the drawer attaches to. Default 'right'. */
  anchor?: 'right' | 'left';
  /** Drawer width. Number is interpreted as px. Default 720. */
  width?: number | string;
  /** Header title. */
  title: string;
  /** Optional descriptive line shown under the title in the header. */
  subtitle?: string;
  /** Footer cancel handler. Footer renders only when both this and `onSave` are provided. */
  onCancel?: () => void;
  /** Footer save handler. May be async (e.g. run validation before closing). */
  onSave?: () => void | Promise<void>;
  /** Disable the save button (e.g. while form invalid). */
  saveDisabled?: boolean;
  /** Render save button in a loading state. */
  isSaving?: boolean;
  /** Drawer body. */
  children: ReactNode;
  /** Forwarded to drawer root for e2e selectors. */
  'data-vy'?: string;
};

const Root = styled(Drawer)({});

const Header = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  padding: theme.spacing(2.5, 3),
  gap: theme.spacing(2),
  borderBottom: `1px solid ${semantic.divider.subtle}`,
}));

const TitleStack = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  minWidth: 0,
}));

const Body = styled('div')(({ theme }) => ({
  flex: 1,
  overflowY: 'auto',
  padding: theme.spacing(3),
  backgroundColor: theme.palette.background.default,
}));

const Footer = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: theme.spacing(1.5),
  padding: theme.spacing(2, 3),
  borderTop: `1px solid ${semantic.divider.subtle}`,
}));

export const VyDrawer = forwardRef<HTMLDivElement, VyDrawerProps>(
  function VyDrawer(
    {
      open,
      onClose,
      anchor = 'right',
      width = 720,
      title,
      subtitle,
      onCancel,
      onSave,
      saveDisabled,
      isSaving,
      children,
      ...rest
    },
    ref,
  ) {
    const showFooter = Boolean(onCancel && onSave);
    return (
      <Root
        ref={ref}
        open={open}
        onClose={onClose}
        anchor={anchor}
        slotProps={{
          paper: { sx: { width, display: 'flex', flexDirection: 'column' } },
        }}
        {...rest}
      >
        <Header>
          <TitleStack>
            <VyTypography variant="h6">{title}</VyTypography>
          </TitleStack>
          <IconButton onClick={onClose} aria-label="close" size="small">
            <XClose width={20} height={20} />
          </IconButton>
        </Header>
        <Body>
          {subtitle && (
            <VyTypography variant="inputHelper" sx={{ display: 'block', color: 'text.secondary', mb: 2.5 }}>
              {subtitle}
            </VyTypography>
          )}
          {children}
        </Body>
        {showFooter && (
          <Footer>
            <VyButton variant="text" onClick={onCancel}>
              Cancel
            </VyButton>
            <VyButton variant="contained" onClick={onSave} disabled={saveDisabled || isSaving}>
              {isSaving ? 'Saving…' : 'Save'}
            </VyButton>
          </Footer>
        )}
      </Root>
    );
  },
);
